import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  IconButton,
  Progress,
  Image,
  Spinner,
  Input,
  useToast,
} from '@chakra-ui/react';
import { X, Heart, Trash2, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import { 
  Story, 
  StoryGroup, 
  viewStory, 
  toggleLikeStory,
  deleteStory,
  addComment,
  formatWinAmount,
  getTimeAgo,
  getWinTier,
} from '../../services/storyService';

interface StoryViewerProps {
  storyGroups: StoryGroup[];
  initialGroupIndex: number;
  currentUserId: string;
  currentUsername?: string;
  currentAvatarUrl?: string;
  onClose: () => void;
  onStoryDeleted?: () => void;
}

const STORY_DURATION = 5000;

const StoryViewer: React.FC<StoryViewerProps> = ({
  storyGroups,
  initialGroupIndex,
  currentUserId,
  currentUsername = 'Anonymous',
  currentAvatarUrl,
  onClose,
  onStoryDeleted,
}) => {
  const toast = useToast();
  const [currentGroupIndex, setCurrentGroupIndex] = useState(initialGroupIndex);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [localIsLiked, setLocalIsLiked] = useState(false);
  const [localLikes, setLocalLikes] = useState(0);

  const currentGroup = storyGroups[currentGroupIndex];
  const currentStory = currentGroup?.stories[currentStoryIndex];
  const isOwnStory = currentStory?.odD === currentUserId;

  // Initialize like state
  useEffect(() => {
    if (currentStory) {
      setLocalIsLiked(currentStory.likedBy?.includes(currentUserId) || false);
      setLocalLikes(currentStory.likes || 0);
    }
  }, [currentStory?.id, currentUserId]);

  // Mark story as viewed
  useEffect(() => {
    if (currentStory && !currentStory.viewedBy?.includes(currentUserId)) {
      viewStory(currentStory.id, currentUserId);
    }
  }, [currentStory?.id, currentUserId]);

  // Reset image state
  useEffect(() => {
    setImageLoaded(false);
  }, [currentStory?.id]);

  // Progress timer
  useEffect(() => {
    if (isPaused || !currentStory || !imageLoaded) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          goToNext();
          return 0;
        }
        return prev + (100 / (STORY_DURATION / 100));
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentStory, isPaused, imageLoaded]);

  // Reset progress on story change
  useEffect(() => {
    setProgress(0);
  }, [currentStoryIndex, currentGroupIndex]);

  const goToNext = useCallback(() => {
    if (!currentGroup) return;

    if (currentStoryIndex < currentGroup.stories.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
    } else if (currentGroupIndex < storyGroups.length - 1) {
      setCurrentGroupIndex(prev => prev + 1);
      setCurrentStoryIndex(0);
    } else {
      onClose();
    }
  }, [currentGroup, currentStoryIndex, currentGroupIndex, storyGroups.length, onClose]);

  const goToPrevious = useCallback(() => {
    if (progress > 20) {
      setProgress(0);
    } else if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
    } else if (currentGroupIndex > 0) {
      const prevGroup = storyGroups[currentGroupIndex - 1];
      setCurrentGroupIndex(prev => prev - 1);
      setCurrentStoryIndex(prevGroup.stories.length - 1);
    }
  }, [progress, currentStoryIndex, currentGroupIndex, storyGroups]);

  const handleLike = async () => {
    if (!currentStory || isLiking) return;
    
    // Optimistic update
    setLocalIsLiked(!localIsLiked);
    setLocalLikes(prev => localIsLiked ? prev - 1 : prev + 1);
    
    setIsLiking(true);
    try {
      await toggleLikeStory(currentStory.id, currentUserId);
    } catch (error) {
      // Revert
      setLocalIsLiked(localIsLiked);
      setLocalLikes(currentStory.likes || 0);
      console.error('Error liking story:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleDelete = async () => {
    if (!currentStory || !isOwnStory) return;
    
    setIsPaused(true);
    try {
      await deleteStory(currentStory.id, currentUserId);
      toast({
        title: 'Story deleted',
        status: 'success',
        duration: 2000,
      });
      onStoryDeleted?.();
      goToNext();
    } catch (error) {
      console.error('Error deleting story:', error);
      toast({
        title: 'Failed to delete',
        status: 'error',
        duration: 2000,
      });
      setIsPaused(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !currentStory || isSendingReply) return;
    
    setIsSendingReply(true);
    setIsPaused(true);
    try {
      await addComment(currentStory.id, currentUserId, currentUsername, currentAvatarUrl, replyText.trim());
      toast({
        title: 'Reply sent!',
        status: 'success',
        duration: 2000,
      });
      setReplyText('');
    } catch (error) {
      console.error('Error sending reply:', error);
      toast({
        title: 'Failed to send',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setIsSendingReply(false);
      setIsPaused(false);
    }
  };

  const handleTap = (e: React.MouseEvent | React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('input')) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0]?.clientX || 0 : e.clientX;
    const x = clientX - rect.left;
    const width = rect.width;
    
    if (x < width / 3) {
      goToPrevious();
    } else if (x > (width * 2) / 3) {
      goToNext();
    }
  };

  // Double tap to like
  const [lastTap, setLastTap] = useState(0);
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap < 300) {
      handleLike();
    }
    setLastTap(now);
  };

  if (!currentStory || !currentGroup) return null;

  const winTier = getWinTier(currentStory.potAmount, currentStory.currency);

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="black"
      zIndex={2000}
      onClick={(e) => { handleTap(e); handleDoubleTap(); }}
      onMouseDown={() => setIsPaused(true)}
      onMouseUp={() => setIsPaused(false)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      {/* Progress Bars */}
      <HStack 
        position="absolute" 
        top={{ base: 2, md: 4 }}
        left={{ base: 2, md: 4 }}
        right={{ base: 2, md: 4 }}
        spacing={1}
        zIndex={10}
      >
        {currentGroup.stories.map((_, index) => (
          <Progress
            key={index}
            value={
              index < currentStoryIndex ? 100 :
              index === currentStoryIndex ? progress : 0
            }
            size="xs"
            flex={1}
            bg="whiteAlpha.300"
            sx={{ '& > div': { bg: 'white', transition: index === currentStoryIndex ? 'none' : 'width 0.3s' } }}
            borderRadius="full"
          />
        ))}
      </HStack>

      {/* Header */}
      <HStack
        position="absolute"
        top={{ base: 6, md: 10 }}
        left={{ base: 2, md: 4 }}
        right={{ base: 2, md: 4 }}
        justify="space-between"
        zIndex={10}
      >
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={currentGroup.username}
            src={currentGroup.avatarUrl}
            border="2px solid white"
          />
          <VStack spacing={0} align="start">
            <Text color="white" fontWeight="bold" fontSize="sm">
              {currentGroup.username}
            </Text>
            <Text color="whiteAlpha.700" fontSize="xs">
              {getTimeAgo(currentStory.createdAt)}
            </Text>
          </VStack>
        </HStack>
        
        <HStack spacing={2}>
          {isOwnStory && (
            <IconButton
              aria-label="Delete"
              icon={<Trash2 size={20} />}
              variant="ghost"
              color="white"
              size="sm"
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
            />
          )}
          <IconButton
            aria-label="Close"
            icon={<X size={24} />}
            variant="ghost"
            color="white"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          />
        </HStack>
      </HStack>

      {/* Navigation Arrows - Desktop only */}
      <Box display={{ base: 'none', md: 'block' }}>
        {(currentGroupIndex > 0 || currentStoryIndex > 0) && (
          <IconButton
            aria-label="Previous"
            icon={<ChevronLeft size={32} />}
            position="absolute"
            left={4}
            top="50%"
            transform="translateY(-50%)"
            variant="ghost"
            color="white"
            size="lg"
            zIndex={10}
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
          />
        )}
        
        {(currentGroupIndex < storyGroups.length - 1 || currentStoryIndex < currentGroup.stories.length - 1) && (
          <IconButton
            aria-label="Next"
            icon={<ChevronRight size={32} />}
            position="absolute"
            right={4}
            top="50%"
            transform="translateY(-50%)"
            variant="ghost"
            color="white"
            size="lg"
            zIndex={10}
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
          />
        )}
      </Box>

      {/* Story Content */}
      <VStack
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        spacing={4}
        maxW="100%"
        w="full"
        px={4}
      >
        {!imageLoaded && <Spinner color="white" size="xl" />}
        
        <Box position="relative" display={imageLoaded ? 'block' : 'none'}>
          <Image
            src={currentStory.imageUrl}
            alt="Story"
            borderRadius={{ base: 0, md: 'lg' }}
            maxH={{ base: '65vh', md: '70vh' }}
            objectFit="contain"
            onLoad={() => setImageLoaded(true)}
          />
          
          {/* Win Badge */}
          <Box
            position="absolute"
            bottom={4}
            left="50%"
            transform="translateX(-50%)"
            bg="blackAlpha.800"
            px={6}
            py={3}
            borderRadius="xl"
            textAlign="center"
            backdropFilter="blur(10px)"
            border="2px solid"
            borderColor={winTier === 'monster' ? POKER_COLORS.gold : 'whiteAlpha.300'}
          >
            <Text
              fontSize={winTier === 'monster' ? '2xl' : 'xl'}
              fontWeight="bold"
              color={winTier === 'monster' ? POKER_COLORS.gold : 'white'}
            >
               {formatWinAmount(currentStory.potAmount, currentStory.currency)}

            </Text>
            {currentStory.handType && (
              <Text color="whiteAlpha.800" fontSize="sm">{currentStory.handType}</Text>
            )}
          </Box>
        </Box>

        {currentStory.caption && (
          <Text color="white" fontSize="md" textAlign="center" maxW="80%">
            {currentStory.caption}
          </Text>
        )}
      </VStack>

      {/* Bottom Actions */}
      <HStack
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        p={4}
        pb={{ base: 6, md: 4 }}
        bg="linear-gradient(transparent, blackAlpha.900)"
        zIndex={10}
        spacing={3}
      >
        {!isOwnStory ? (
          <>
            <Input
              placeholder="Reply to story..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onFocus={() => setIsPaused(true)}
              onBlur={() => !replyText && setIsPaused(false)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
              bg="whiteAlpha.200"
              border="none"
              color="white"
              borderRadius="full"
              _placeholder={{ color: 'whiteAlpha.600' }}
              _focus={{ bg: 'whiteAlpha.300' }}
              flex={1}
            />
            {replyText.trim() ? (
              <IconButton
                aria-label="Send"
                icon={<Send size={20} />}
                colorScheme="cyan"
                borderRadius="full"
                isLoading={isSendingReply}
                onClick={(e) => { e.stopPropagation(); handleSendReply(); }}
              />
            ) : (
              <IconButton
                aria-label="Like"
                icon={<Heart size={24} fill={localIsLiked ? '#ef4444' : 'none'} />}
                variant="ghost"
                color={localIsLiked ? 'red.400' : 'white'}
                isLoading={isLiking}
                onClick={(e) => { e.stopPropagation(); handleLike(); }}
              />
            )}
          </>
        ) : (
          <HStack justify="center" w="full" spacing={8}>
            <VStack spacing={0}>
              <Text color="white" fontSize="xl" fontWeight="bold">
                {currentStory.views || 0}
              </Text>
              <Text color="whiteAlpha.600" fontSize="xs">views</Text>
            </VStack>
            <VStack spacing={0}>
              <Text color="white" fontSize="xl" fontWeight="bold">
                {localLikes}
              </Text>
              <Text color="whiteAlpha.600" fontSize="xs">likes</Text>
            </VStack>
            <VStack spacing={0}>
              <Text color="white" fontSize="xl" fontWeight="bold">
                {(currentStory.comments || []).length}
              </Text>
              <Text color="whiteAlpha.600" fontSize="xs">replies</Text>
            </VStack>
          </HStack>
        )}
      </HStack>
    </Box>
  );
};

export default StoryViewer;